import { useCallback, useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { container } from "tsyringe";
import ForumPostResponseResource from "common/resources/Project/Config/Forum/ForumPostResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import { useProjectId } from "hooks/useProjectId";
import ForumPostLikeService from "services/projects/ForumPostLikeService";
import classes from "./classes.module.scss";

type IProps = {
	post: Pick<ForumPostResponseResource, "id">;
};

const forumPostLikeService = container.resolve(ForumPostLikeService);

export default function ForumPostLikeButton(props: IProps) {
	const { isLiked, toggleLike, getLikesCount } = useLike(props.post);

	const handleClickToggleLike = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.stopPropagation();
			e.preventDefault();

			return toggleLike();
		},
		[toggleLike],
	);

	return isLiked ? (
		<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} className={classes["root"]} onClick={handleClickToggleLike}>
			<Heart fill={"black"} className={classes["icon"]} />
			{getLikesCount()}
		</Button>
	) : (
		<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} className={classes["root"]} onClick={handleClickToggleLike}>
			<Heart className={classes["icon"]} />
			{getLikesCount()}
		</Button>
	);
}

const useLike = (post: Pick<ForumPostResponseResource, "id">) => {
	const { projectId } = useProjectId();

	const [previouslyLikedCount, setPreviouslyLikedCount] = useState<number>(0);
	const [isPreviouslyLiked, setIsPreviouslyLiked] = useState<boolean>(false);
	const [isLiked, setIsLiked] = useState<boolean>(false);

	const toggleLike = useCallback(async () => {
		return forumPostLikeService[isLiked ? "unlike" : "like"](projectId, post.id).then(() => {
			setIsLiked((isLiked) => !isLiked);
		});
	}, [isLiked, projectId, post.id]);

	const getLikesCount = useCallback(() => {
		return isLiked ? previouslyLikedCount + (isPreviouslyLiked ? 0 : 1) : previouslyLikedCount - (isPreviouslyLiked ? 1 : 0);
	}, [isLiked, isPreviouslyLiked, previouslyLikedCount]);

	const fetchLikesCount = useCallback(() => {
		forumPostLikeService.count(projectId, post.id).then(({ count }) => setPreviouslyLikedCount(count));
	}, [post.id, projectId]);

	const fetchIsPreviouslyLiked = useCallback(() => {
		forumPostLikeService.isLiked(projectId, post.id).then(({ isLiked }) => {
			setIsLiked(isLiked);
			setIsPreviouslyLiked(isLiked);
		});
	}, [post.id, projectId]);

	useEffect(() => {
		fetchIsPreviouslyLiked();
	}, [fetchIsPreviouslyLiked]);

	useEffect(() => {
		fetchLikesCount();
	}, [fetchLikesCount]);

	return {
		isLiked,
		toggleLike,
		getLikesCount,
	};
};
